/**
 * FullFilter에 전달할 sections 배열 생성
 *
 * FullFilter 렌더러는 부동산 유형을 모른다.
 * 이 함수에서 선택된 카테고리에 따라 보여줄 섹션을 결정하고,
 * 결과 배열을 그대로 렌더링만 하면 된다.
 *
 * @param {Object} opts
 * @param {string} opts.selectedCategory — 선택된 카테고리 value (예: "10,11,17,18,19,15") 또는 null(전체)
 */
import {
  ESTATE_CATEGORIES,
  DEAL_TYPES,
  USE_AREA_GROUPS,
  FLOOR_OPTIONS,
  DIRECTION_OPTIONS,
  APPROVAL_OPTIONS,
  AVAILABLE_DATE_OPTIONS,
  ROOM_COUNT_OPTIONS,
  BATHROOM_COUNT_OPTIONS,
  PRICE_CONFIG,
  AREA_CONFIG,
  M2_TO_PYEONG,
  formatPrice,
  formatPriceInput,
  parsePrice,
  convertPriceLabel,
} from "./filterOptions";

// 카테고리별 표시할 추가 섹션 매핑
// 피그마 기준 순서: approval → useArea → floor → availableDate → interior → direction
const CATEGORY_SECTIONS = {
  // 상가・사무실・공장: 사용승인일, 용도지역, 층수
  "10,11,17,18,19,15": ["approval", "useArea", "floor"],
  // 아파트・오피스텔: 사용승인일, 용도지역, 층수, 입주가능일, 내부구조, 방향
  "0,1,2": ["approval", "useArea", "floor", "availableDate", "interior", "direction"],
  // 빌라・주택: 사용승인일, 용도지역, 층수, 입주가능일, 내부구조, 방향
  "7,8,3,4,5,6": ["approval", "useArea", "floor", "availableDate", "interior", "direction"],
  // 토지: 용도지역만
  "16": ["useArea"],
};

// 전체유형: 피그마에서 용도지역만 표시
const ALL_EXTRA_SECTIONS = ["useArea"];

export function buildFilterSections({ selectedCategory = null } = {}) {
  const extraSections = selectedCategory
    ? (CATEGORY_SECTIONS[selectedCategory] || ["useArea"])
    : ALL_EXTRA_SECTIONS;

  const sections = [
    // ── 매물유형 ──
    {
      id: "estateType",
      section: "매물유형",
      filters: [
        {
          id: "randType",
          label: "매물 유형",
          type: "category-tab",
          categories: ESTATE_CATEGORIES,
        },
      ],
    },

    // ── 거래유형 ──
    {
      id: "dealType",
      section: "거래유형",
      filters: [
        {
          id: "deal_type",
          label: "거래 유형",
          type: "chip",
          options: DEAL_TYPES,
          multiple: true,
          showAll: true,
        },
      ],
    },

    // ── 가격 (매매 + 보증금 + 월세) ──
    // 거래유형별 표시: 매매(0)→매매가, 전세(1)→보증금, 월세(2)→보증금+월세, 전체→전부
    {
      id: "price",
      section: "가격",
      connectedAbove: true,
      filters: [
        {
          id: "price",
          label: "매매",
          type: "range",
          visible: (filter) => {
            const dt = filter.deal_type || [];
            return dt.length === 0 || dt.includes(0) || dt.includes("0");
          },
          min: PRICE_CONFIG.sale.min,
          max: PRICE_CONFIG.sale.max,
          step: PRICE_CONFIG.sale.step,
          minKey: "minPrice",
          maxKey: "maxPrice",
          unit: "만원",
          format: formatPriceInput,
          parse: parsePrice,
          convertLabel: convertPriceLabel,
          minPlaceholder: "최소",
          maxPlaceholder: "최대",
          minLabel: "최소",
          maxLabel: `${formatPrice(PRICE_CONFIG.sale.max)}+`,
        },
        {
          id: "deposit",
          label: "보증금 (전세금)",
          type: "range",
          visible: (filter) => {
            const dt = filter.deal_type || [];
            return dt.length === 0 || dt.includes(1) || dt.includes("1") || dt.includes(2) || dt.includes("2");
          },
          min: PRICE_CONFIG.deposit.min,
          max: PRICE_CONFIG.deposit.max,
          step: PRICE_CONFIG.deposit.step,
          minKey: "minDeposit",
          maxKey: "maxDeposit",
          unit: "만원",
          format: formatPriceInput,
          parse: parsePrice,
          convertLabel: convertPriceLabel,
          minPlaceholder: "최소",
          maxPlaceholder: "최대",
          minLabel: "최소",
          maxLabel: `${formatPrice(PRICE_CONFIG.deposit.max)}+`,
        },
        {
          id: "monthly",
          label: "월세",
          type: "range",
          visible: (filter) => {
            const dt = filter.deal_type || [];
            return dt.length === 0 || dt.includes(2) || dt.includes("2");
          },
          min: PRICE_CONFIG.monthly.min,
          max: PRICE_CONFIG.monthly.max,
          step: PRICE_CONFIG.monthly.step,
          minKey: "minMonthly",
          maxKey: "maxMonthly",
          unit: "만원",
          format: formatPriceInput,
          parse: parsePrice,
          convertLabel: convertPriceLabel,
          minPlaceholder: "최소",
          maxPlaceholder: "최대",
          minLabel: "최소",
          maxLabel: `${formatPrice(PRICE_CONFIG.monthly.max)}+`,
        },
      ],
    },

    // ── 면적 ──
    // 피그마: 섹션라벨 "면적"(blue) + 서브헤더 "면적 범위"(bold) + UnitToggle
    {
      id: "area",
      section: "면적",
      filters: [
        {
          id: "area",
          label: "면적",
          type: "range",
          rangeTitle: "면적 범위",
          min: AREA_CONFIG.pyeong.min,
          max: AREA_CONFIG.pyeong.max,
          step: AREA_CONFIG.pyeong.step,
          minKey: "minArea",
          maxKey: "maxArea",
          minPlaceholder: "최소",
          maxPlaceholder: "최대",
          minLabel: "최소",
          maxLabel: "최대",
          // 값은 항상 m²로 저장. 평 모드에서는 format/parse가 변환 처리
          unitToggle: [
            {
              label: "평",
              value: "pyeong",
              rangeConfig: AREA_CONFIG.sqm,
              format: (v) => (v != null ? `${Math.round(v * M2_TO_PYEONG)}` : ""),
              parse: (s) => { const n = parseFloat(s); return isNaN(n) ? null : Math.round(n / M2_TO_PYEONG); },
            },
            {
              label: "m²",
              value: "sqm",
              rangeConfig: AREA_CONFIG.sqm,
              format: (v) => (v != null ? `${v}` : ""),
              parse: (s) => { const n = parseFloat(s); return isNaN(n) ? null : n; },
            },
          ],
          unitKey: "areaUnit",
          checkbox: {
            label: "전용면적 기준으로 찾기",
            key: "exclusiveAreaOnly",
          },
        },
      ],
    },
  ];

  // ── 조건부 섹션 (카테고리별 표시 순서 유지) ──

  if (extraSections.includes("approval")) {
    sections.push({
      id: "approval",
      section: "사용승인일",
      sectionPy: 16,
      filters: [
        {
          id: "approvalYear",
          label: "사용 승인일",
          type: "radio",
          options: APPROVAL_OPTIONS,
          showAll: true,
        },
      ],
    });
  }

  if (extraSections.includes("useArea")) {
    sections.push({
      id: "useArea",
      section: "용도지역",
      filters: [
        {
          id: "useArea",
          label: "용도지역",
          type: "grouped-chip",
          groups: USE_AREA_GROUPS,
        },
      ],
    });
  }

  if (extraSections.includes("floor")) {
    sections.push({
      id: "floor",
      section: "층수",
      sectionPy: 16,
      filters: [
        {
          id: "floorType",
          label: "층수",
          type: "chip",
          options: FLOOR_OPTIONS,
          multiple: true,
          showAll: true,
          chipSize: "md",
        },
      ],
    });
  }

  if (extraSections.includes("availableDate")) {
    sections.push({
      id: "availableDate",
      section: "입주가능일",
      sectionPy: 16,
      filters: [
        {
          id: "availableDate",
          label: "입주 가능일",
          type: "radio",
          options: AVAILABLE_DATE_OPTIONS,
          showAll: false,
        },
      ],
    });
  }

  if (extraSections.includes("interior")) {
    sections.push({
      id: "interior",
      section: "내부구조",
      sectionPy: 16,
      filters: [
        {
          id: "roomCnt",
          label: "내부구조",
          type: "interior-group",
          groups: [
            { label: "방수", options: ROOM_COUNT_OPTIONS },
            { label: "욕실수", options: BATHROOM_COUNT_OPTIONS },
          ],
        },
      ],
    });
  }

  if (extraSections.includes("direction")) {
    sections.push({
      id: "direction",
      section: "방향",
      sectionPy: 16,
      filters: [
        {
          id: "direction",
          label: "방향",
          type: "chip",
          options: DIRECTION_OPTIONS,
          multiple: true,
          showAll: true,
          chipSize: "md",
        },
      ],
    });
  }

  return sections;
}
